
import React, { useState, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { AGENT_COMMANDS, AgentCommand } from "@/constants/AgentCommandsConfig";
import { Search, Terminal, Sparkles, Wand2, Settings, Code, Activity } from "lucide-react";
import "./AgentInteractionDialog.scss";

interface AgentInteractionDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSelectCommand: (command: string) => void;
}

const AgentInteractionDialog: React.FC<AgentInteractionDialogProps> = ({ isOpen, onClose, onSelectCommand }) => {
    const [searchQuery, setSearchQuery] = useState("");

    const filteredCommands = useMemo(() => {
        if (!searchQuery.trim()) return AGENT_COMMANDS;

        return AGENT_COMMANDS.map(category => ({
            ...category,
            commands: category.commands.filter(cmd =>
                cmd.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
                cmd.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                cmd.description?.toLowerCase().includes(searchQuery.toLowerCase())
            )
        })).filter(category => category.commands.length > 0);
    }, [searchQuery]);

    const handleCommandClick = (commandId: string) => {
        onSelectCommand(commandId);
        onClose();
        setSearchQuery("");
    };

    const getCategoryIcon = (category: string) => {
        switch (category) {
            case "Agents (Personas)": return <Sparkles className="w-4 h-4" />;
            case "Discovery & Planning": return <Wand2 className="w-4 h-4" />;
            case "Implementation & Quality": return <Code className="w-4 h-4" />;
            case "Testing (TestArch)": return <Activity className="w-4 h-4" />;
            case "Utilities & Diagrams": return <Settings className="w-4 h-4" />;
            default: return <Terminal className="w-4 h-4" />;
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="agent-interaction-dialog max-w-2xl max-h-[80vh] flex flex-col p-0 overflow-hidden border-neutral-800 bg-[#0A0A0A]">
                <DialogHeader className="p-6 pb-2 border-b border-neutral-800">
                    <DialogTitle className="flex items-center gap-2 text-xl font-semibold text-neutral-100">
                        <Terminal className="w-5 h-5 text-primary" />
                        Execute Workflow Command
                    </DialogTitle>
                    <div className="relative mt-4">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                        <Input
                            className="pl-10 h-11 bg-neutral-900 border-neutral-700 text-neutral-200 placeholder:text-neutral-500 focus:ring-primary/20"
                            placeholder="Search agents, workflows, or slash commands..."
                            value={searchQuery}
                            onChange={(val: string) => setSearchQuery(val)}
                            autoFocus
                        />
                    </div>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-2 scrollbar-premium">
                    {filteredCommands.length > 0 ? (
                        filteredCommands.map((category, idx) => (
                            <div key={idx} className="mb-4 last:mb-2">
                                <h3 className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-neutral-500 flex items-center gap-2">
                                    {getCategoryIcon(category.category)}
                                    {category.category}
                                </h3>
                                <div className="grid grid-cols-1 gap-1">
                                    {category.commands.map((cmd: AgentCommand) => (
                                        <button
                                            key={cmd.id}
                                            className="group flex flex-col items-start px-4 py-3 rounded-lg hover:bg-neutral-900/50 transition-all duration-200 border border-transparent hover:border-neutral-800 text-left"
                                            onClick={() => handleCommandClick(cmd.id)}
                                        >
                                            <div className="flex items-center gap-2 w-full">
                                                <span className="text-sm font-medium text-neutral-200 group-hover:text-primary transition-colors">
                                                    {cmd.label}
                                                </span>
                                                <span className="text-[10px] font-mono text-neutral-600 bg-neutral-900 px-1.5 py-0.5 rounded ml-auto group-hover:text-neutral-400">
                                                    {cmd.id.split(':').pop()}
                                                </span>
                                            </div>
                                            {cmd.description && (
                                                <p className="text-xs text-neutral-500 mt-1 line-clamp-1 group-hover:text-neutral-400">
                                                    {cmd.description}
                                                </p>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-neutral-500">
                            <Search className="w-12 h-12 mb-4 opacity-10" />
                            <p className="text-sm">No commands matching "{searchQuery}"</p>
                        </div>
                    )}
                </div>

                <div className="p-4 border-t border-neutral-800 bg-neutral-900/30 flex items-center justify-between">
                    <div className="flex items-center gap-4 text-[10px] text-neutral-600">
                        <span className="flex items-center gap-1.5">
                            <kbd className="px-1.5 py-0.5 rounded bg-neutral-800 border border-neutral-700 font-mono">↑↓</kbd>
                            Navigate
                        </span>
                        <span className="flex items-center gap-1.5">
                            <kbd className="px-1.5 py-0.5 rounded bg-neutral-800 border border-neutral-700 font-mono">Enter</kbd>
                            Select
                        </span>
                        <span className="flex items-center gap-1.5">
                            <kbd className="px-1.5 py-0.5 rounded bg-neutral-800 border border-neutral-700 font-mono">Esc</kbd>
                            Close
                        </span>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AgentInteractionDialog;
